import { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classes from './ImportFile.module.css';
import clsx from 'clsx';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import Select from 'Components/Atoms/Select/Select';
import Checkbox from 'Components/Atoms/Checkbox/Checkbox';
import { Trans, useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';

const VALUE_NONE = 'none';

export default function ImportFileMapper(props) {
    const {
        onClose,
        importId,
        onSubmit,
        columns,
        onChangeProcessing,
        processing,
        buttonText,
        mapService,
        suggestion,
        fields: initialFields,
    } = props;

    const [error, setError] = useState(null);

    const { t } = useTranslation('common');

    const [fields, setFields] = useState(() => {
        return initialFields.map((item) => {
            const columnIndex = suggestion?.columns_mapping?.[item.id];
            const column = columnIndex !== null && columnIndex !== undefined ? columns[columnIndex] : null;

            if (!column) {
                return {
                    ...item,
                    value: VALUE_NONE,
                    deduplicationColumnsValue: false,
                };
            }

            return {
                ...item,
                value: column.id,
                deduplicationColumnsValue: item.unique || suggestion.deduplication_columns.includes(item.id),
            };
        });
    });

    const [suggestionApplied] = useState(() => fields.some((item) => item.value !== VALUE_NONE));

    const { showUnknownError } = useErrors();

    const handleSubmit = async () => {
        const allRequiredHasValue = fields.every((item) => !item.required || item.value !== VALUE_NONE);

        if (!allRequiredHasValue) {
            setError(t('import-error-required-fields'));
            return;
        }

        onChangeProcessing(true);
        setError(null);

        try {
            const deduplicationColumns = fields.filter((item) => item.deduplicationColumnsValue).map((item) => item.id);

            const mappedColumns = fields.reduce(
                (acc, item) => {
                    if (item.value !== VALUE_NONE) {
                        acc[item.id] = columns.findIndex((column) => column.id === item.value);
                    } else {
                        acc[item.id] = null;
                    }

                    return acc;
                },
                {
                    deduplication_columns: deduplicationColumns,
                },
            );

            const response = await mapService(importId, mappedColumns);
            onSubmit(response);
        } catch (error) {
            showUnknownError(error);
            onClose();
        } finally {
            onChangeProcessing(false);
        }
    };

    const noneOption = {
        value: VALUE_NONE,
        label: `- ${t('column-none')} -`,
    };

    const columnOptions = columns.map((item) => ({
        value: item.id,
        label: (
            <div>
                <div>{item.id}</div>
                <div className="text-xs opacity-50">{'"' + item.sample + '"'}</div>
            </div>
        ),
    }));

    const options = [noneOption, ...columnOptions];

    const handleChange = (name, value) => {
        setError(null);

        setFields((prev) => {
            const newFields = prev.map((field) => {
                if (field.id === name) {
                    if (value === VALUE_NONE) {
                        return {
                            ...field,
                            deduplicationColumnsValue: false,
                            value,
                        };
                    }

                    if (field.unique) {
                        return {
                            ...field,
                            deduplicationColumnsValue: true,
                            value,
                        };
                    }

                    return {
                        ...field,
                        value,
                    };
                }

                return field;
            });

            return newFields;
        });
    };

    const handleChangeCheckbox = (ev) => {
        const { name, checked } = ev.target;

        setFields((prev) => {
            const newFields = prev.map((field) => {
                if (`checkbox-${field.id}` === name) {
                    return {
                        ...field,
                        deduplicationColumnsValue: checked,
                    };
                }

                return field;
            });

            return newFields;
        });
    };

    return (
        <div className="flex flex-col h-full min-h-0">
            <div className="grow min-h-0 overflow-y-auto pt-4">
                {suggestionApplied && (
                    <div className="text-xs opacity-50 mb-4">{t('import-mapping-suggestion-review')}</div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <div className="uppercase font-semibold text-2xs tracking-widest">{t('mapper-data')}</div>
                    <div className="uppercase font-semibold text-2xs tracking-widest">{t('mapper-column')}</div>
                    {fields.map((item) => (
                        <Fragment key={item.id}>
                            <div className={classes.box}>
                                {item.label}
                                {item.required && (
                                    <>
                                        <span className="text-danger"> *</span>
                                        <div className="text-xs opacity-50">{t('required')}</div>
                                    </>
                                )}
                            </div>

                            <Select
                                className="w-full"
                                options={options}
                                value={item.value}
                                onChange={(value) => handleChange(item.id, value)}
                            />
                            {item.deduplicationColumns && item.value !== VALUE_NONE && (
                                <div className={clsx(classes.fullWidth, 'pb-1')}>
                                    <Checkbox
                                        checked={item.deduplicationColumnsValue}
                                        name={`checkbox-${item.id}`}
                                        onChange={handleChangeCheckbox}
                                        disabled={item.unique}
                                    >
                                        <Trans t={t} i18nKey="import-deduplication">
                                            Do not import with <strong>{{ column: item.label }}</strong> repeated
                                        </Trans>
                                    </Checkbox>
                                </div>
                            )}
                        </Fragment>
                    ))}
                </div>
            </div>
            {error && <div className={clsx(classes.error, 'text-center')}>{error}</div>}
            <div className="pt-4">
                <Button isBusy={processing} variant="primary" className="w-full" onClick={handleSubmit}>
                    {buttonText}
                </Button>
            </div>
        </div>
    );
}

ImportFileMapper.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onChangeProcessing: PropTypes.func.isRequired,
    processing: PropTypes.bool,
    columns: PropTypes.array,
    importId: PropTypes.string,
    buttonText: PropTypes.string,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            required: PropTypes.bool,
            deduplicationColumns: PropTypes.bool,
            label: PropTypes.string,
        }),
    ).isRequired,
    mapService: PropTypes.func.isRequired,
    suggestion: PropTypes.shape({
        columns_mapping: PropTypes.object,
        deduplication_columns: PropTypes.arrayOf(PropTypes.string),
    }),
};
